import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { loadMediaRecommendationPreferencesFromCookie } from './media-recommendation-preferences-cookie-operations';

import type { MediaOptions } from 'js/src/client';
import { MusicRecommendationModeEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { storeWorkspaces } from 'js/src/data/account/account-actions';
import Flags from 'js/src/libs/flags';
import { hasMediaGuidance } from 'js/src/libs/mediaGuidanceUtils';

type MediaRecommendationPreferencesState = MediaOptions & {
    music_recommendation_mode: MusicRecommendationModeEnum;
    media_guidance_override: string | null;
};

const getSystemDefaults = (): MediaRecommendationPreferencesState => {
    const isDamAvailable = Flags.canSeeDamTab();
    return {
        source_media_enabled: true,
        stock_media_enabled: true,
        stock_icons_enabled: true,
        dam_media_enabled: isDamAvailable,
        workspace_media_mode: WorkspaceMediaModeEnum.OFF,
        workspace_folder_ids: [],
        media_guidance: '',
        minimum_stock_suitability_score: null,
        music_recommendation_mode: MusicRecommendationModeEnum.RECOMMENDED,
        media_guidance_override: null,
    };
};

const getInitialState = (): MediaRecommendationPreferencesState => {
    // Try to load from cookies first
    const cookiePrefs = loadMediaRecommendationPreferencesFromCookie();
    if (cookiePrefs) {
        return {
            ...cookiePrefs,
            music_recommendation_mode:
                cookiePrefs.music_recommendation_mode ?? MusicRecommendationModeEnum.RECOMMENDED,
            media_guidance_override: null,
        };
    }
    // Otherwise use system defaults
    return getSystemDefaults();
};

const initialState: MediaRecommendationPreferencesState = getInitialState();

const mediaRecommendationPreferencesSlice = createSlice({
    name: 'mediaRecommendationPreferences',
    initialState,
    reducers: {
        setStockMediaEnabled: (state, action: PayloadAction<boolean>) => {
            state.stock_media_enabled = action.payload;
        },
        setDamMediaEnabled: (state, action: PayloadAction<boolean>) => {
            state.dam_media_enabled = action.payload;
        },
        setWorkspaceMediaMode: (state, action: PayloadAction<WorkspaceMediaModeEnum>) => {
            state.workspace_media_mode = action.payload;
        },
        setWorkspaceFolderIds: (state, action: PayloadAction<number[]>) => {
            state.workspace_folder_ids = action.payload;
        },
        setMediaGuidance: (state, action: PayloadAction<string>) => {
            state.media_guidance = action.payload;
        },
        // Transient override for media_guidance that only applies to the next video creation.
        // Used by media concepts to avoid persisting concept-specific guidance to cookies,
        // which would overwrite workspace guidance and incorrectly apply to future videos.
        // Cleared by CreateVideo tools after consumption.
        setMediaGuidanceOverride: (state, action: PayloadAction<string>) => {
            state.media_guidance_override = action.payload;
        },
        clearMediaGuidanceOverride: (state) => {
            state.media_guidance_override = null;
        },
        setMinimumStockSuitabilityScore: (state, action: PayloadAction<number | null>) => {
            state.minimum_stock_suitability_score = action.payload;
        },
        setMusicRecommendationMode: (state, action: PayloadAction<MusicRecommendationModeEnum>) => {
            state.music_recommendation_mode = action.payload;
        },
        resetMediaSourcesToDefaults: (state) => {
            const defaults = getSystemDefaults();
            state.stock_media_enabled = defaults.stock_media_enabled;
            state.dam_media_enabled = defaults.dam_media_enabled;
            state.workspace_media_mode = defaults.workspace_media_mode;
            state.workspace_folder_ids = defaults.workspace_folder_ids;
            state.media_guidance = defaults.media_guidance;
        },
    },
    extraReducers: (builder) => {
        // Listen for workspace loaded/updated events to set media_guidance default
        builder.addCase(storeWorkspaces, (state, action) => {
            // When workspaces are loaded, seed media_guidance from the workspace when
            // the user hasn't set any real guidance of their own
            if (!hasMediaGuidance(state.media_guidance)) {
                const activeWorkspaceId = window.SERVER?.USER?.workspace_id;
                if (activeWorkspaceId) {
                    const activeWorkspace = action.payload.workspaces.find(
                        (ws) => ws.get('id') === activeWorkspaceId,
                    );
                    if (activeWorkspace) {
                        const aiContextEnabled = activeWorkspace.get('ai_context_enabled');
                        const workspaceMediaGuidance = activeWorkspace.get('media_guidance');
                        if (aiContextEnabled === true && workspaceMediaGuidance) {
                            state.media_guidance = workspaceMediaGuidance;
                        }
                    }
                }
            }
        });
    },
});

export const {
    setStockMediaEnabled,
    setDamMediaEnabled,
    setWorkspaceMediaMode,
    setWorkspaceFolderIds,
    setMediaGuidance,
    setMediaGuidanceOverride,
    clearMediaGuidanceOverride,
    setMinimumStockSuitabilityScore,
    setMusicRecommendationMode,
    resetMediaSourcesToDefaults,
} = mediaRecommendationPreferencesSlice.actions;

export default mediaRecommendationPreferencesSlice.reducer;
